import { EMPTY_ENCODING, EMPTY_MARKS, EncField, Encoding, MarkType, VariableType } from '@mode-vegas/vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { ChartDomain, MetricDomain } from '@mode/shared/contract-common';
import { DEFAULT_CHART_FORMAT, generateUUID } from '@mode/shared/util-js';
import { CompiledField, CompiledFormula, Switch } from '@mode/shared/util-switch';
import { EncodingHelpers } from '@mode/charts/util-vegas';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { getUpdatedTableFormat } from '@mode/charts/domain';

/**
 * @description Create a short uuid as a local replacement for @mode-vegas/vegas's BField ID function
 */
function shortId() {
  return generateUUID().slice(0, 6).toLowerCase();
}

/**
 * @description Throws if arg is null|undefined, asserting the type is non-null
 */
function asserts<T>(arg: T | void | undefined | null, m = 'arg'): asserts arg is T {
  if (arg == null) throw new Error(`Expected ${m} to be defined`);
}

/**
 * @description Generate Dbt [Metric] SQL Given a Metric's MacroSpec
 */
export function createDbtSqlFromSpec(spec: MetricDomain.MacroSpec): string {
  const dimensions = spec.dimensions.map((dim) => `'${dim}'`).sort();

  /**
   * Explicitly define the target list such that the casing doesn't change out from
   * underneath us. While free standing identifiers in SQL are case insensitive, this
   * is not the case in Switch; Switch ident/access expressions are case sensitive
   */

  const terms: string[] = [
    // date_<grain> -> timestamp
    `date_${spec.grain} as "${spec.timestamp}"`,
    // metric
    `${spec.name} as "${spec.name}"`,
  ];

  for (const name of spec.dimensions) {
    terms.push(`${name} as "${name}"`);
  }

  return `
{% raw %}
  SELECT ${terms}
  FROM {{
    metrics.calculate(
      metric('${spec.name}'),
      dimensions=[${dimensions}],
      grain='${spec.grain}',
    )
  }}
{% endraw %}
`.trim();
}

/**
 * @description Create a @mode-switch/model with a View from a metric
 */
export function createModel(metric: MetricDomain.Metric): FlamingoTypes.ModeModel {
  return Switch.Models.makeFromView(metric.token, createView(metric));
}

/**
 * @description Create a @mode-switch/model#view from a metric
 */
export function createView(metric: MetricDomain.Metric): FlamingoTypes.ModeView {
  return Switch.Views.make(
    metric.token,
    metric.token,
    metric.attrs.map((attr) => {
      return {
        type: attr.dataType,
        name: attr.name,
      };
    })
  );
}

/**
 * @description Create a MacroSpec given a metric and encoding
 * - Grain
 *   - Metric timestamps should be encoded as a datetrunc*. Find datetrunc with an attr
 *     name matching the metric's timestamp; map the formula's DateTime.Unit to a grain
 * - Dimensions
 *   - Collect EncFields of the encoding per the mark channel (x|y|*)
 *   - Collect distinct attr names from [compiled?] EncField formulas
 *   - Omit timestamp/metric from this list
 */
export function createMacroSpecFromEncoding(metric: MetricDomain.Metric, enc: Encoding): MetricDomain.MacroSpec {
  const timestampAttr = metric.attrs.find((a) => a.type === MetricDomain.AttrType.Timestamp);
  const metricAttr = metric.attrs.find((a) => a.type === MetricDomain.AttrType.Metric);
  const encFields = EncodingHelpers.collectEncFields(enc);
  const view = createView(metric);
  const dims = new Set<string>();

  asserts(timestampAttr, 'timestamp');
  asserts(metricAttr, 'metric');

  // Pluck from datetrunc over timestamp field
  let grain = metric.timeGrains[0];

  for (const field of encFields) {
    const compiled = CompiledField.fromEncField(view, field);

    // Must compile
    if (!compiled.isValid) {
      throw new Error(`[metrics]: could not compile EncField with formula "${field.formula}"`);
    }

    // Must have an attrName
    const attrName = compiled.attrName;
    if (attrName === undefined) {
      throw new Error(`[metrics]: could not resolve attrName of EncField with formula "${field.formula}"`);
    }

    // Found the timestamp, get the grain
    const isTimestamp = attrName === timestampAttr.name;
    if (isTimestamp && compiled.hasDateTrunc) {
      grain = compiled.dateFuncUnit?.toLowerCase() ?? grain;
    }

    // Collect
    dims.add(attrName);
  }

  // Grain must exist in the set of available grains
  const isAllowedGrain = metric.timeGrains.includes(grain);
  if (!isAllowedGrain) {
    throw new Error(`[metrics]: expected time grain to be one of [${metric.timeGrains}], saw "${grain}"`);
  }

  ///
  ///

  const name = metricAttr.name;
  const timestamp = timestampAttr.name;
  const dimensions = Array.from(dims).filter((name) =>
    metric.attrs.some((attr) => {
      return attr.name === name && attr.type === MetricDomain.AttrType.Dimension;
    })
  );

  return {
    name,
    grain,
    timestamp,
    dimensions,
    startDate: undefined,
    endDate: undefined,
    where: [],
  };
}

/**
 * @description Create a MacroSpec given a metric and vizType
 * - Grain
 *   - Use first value of metric's timeGrains
 * - Table (vizType)
 *   - `dimensions` = all metric attrs minus the metric/timestamp
 * - Bar/Line (vizType)
 *   - `dimensions` = []
 */
export function createMacroSpecFromMetric(
  metric: MetricDomain.Metric,
  vizType: MetricDomain.VizTypeSelection
): MetricDomain.MacroSpec {
  const timestampAttr = metric.attrs.find((a) => a.type === MetricDomain.AttrType.Timestamp);
  const metricAttr = metric.attrs.find((a) => a.type === MetricDomain.AttrType.Metric);
  const timeGrain = metric.timeGrains[0];

  asserts(timestampAttr, 'timestamp');
  asserts(metricAttr, 'metric');
  asserts(timeGrain, 'grain');

  const timestamp = timestampAttr.name;
  const name = metricAttr.name;
  const grain = timeGrain;

  switch (vizType) {
    case 'table': {
      // Attrs sans metric n' timestamp
      const dimensions = metric.attrs
        .filter((attr) => attr.type === MetricDomain.AttrType.Dimension)
        .map((attr) => attr.name);
      return {
        name,
        grain,
        timestamp,
        dimensions,
        startDate: undefined,
        endDate: undefined,
        where: [],
      };
    }
    case 'line':
    case 'bar': {
      const dimensions: string[] = [];
      return {
        name,
        grain,
        timestamp,
        dimensions,
        startDate: undefined,
        endDate: undefined,
        where: [],
      };
    }
  }
}

export function createChart(
  metric: MetricDomain.Metric,
  vizType: MetricDomain.VizTypeSelection,
  colorPaletteToken: string,
  chartFormat = DEFAULT_CHART_FORMAT
): ChartDomain.Chart {
  const model = createModel(metric);
  const chartType: ChartTypes.ChartType = vizType === 'table' ? 'table' : 'vegas';
  const encoding = createEncoding(metric, vizType);
  const chartFormats =
    vizType === 'table'
      ? getUpdatedTableFormat(model, model.name, encoding, {}, chartFormat)
      : { fieldFormats: {}, format: chartFormat };

  return {
    chartType,
    version: 2,
    token: metric.token,
    switchViewToken: metric.token,
    colorPaletteToken: colorPaletteToken,
    encoding,
    fieldColors: {},
    facetSizes: {},
    title: metric.label ?? '',
    description: metric.desc ?? '',
    caption: ChartTypes.EMPTY_CAPTION,
    createdAt: new Date().toISOString(),
    isAutoUpdateDisabled: true,
    ...chartFormats,
  };
}

export function createEncoding(metric: MetricDomain.Metric, vizType: MetricDomain.VizTypeSelection): Encoding {
  const timestampAttr = metric.attrs.find((a) => a.type === MetricDomain.AttrType.Timestamp);
  const metricAttr = metric.attrs.find((a) => a.type === MetricDomain.AttrType.Metric);
  const timeGrain = metric.timeGrains[0];

  asserts(timestampAttr, 'timestamp');
  asserts(metricAttr, 'metric');
  asserts(timeGrain, 'grain');

  switch (vizType) {
    case 'table': {
      // Attrs sans metric n' timestamp
      const dimAttrs = metric.attrs.filter((attr) => {
        return attr.type === MetricDomain.AttrType.Dimension;
      });

      return <Encoding>{
        ...EMPTY_ENCODING,
        marks: {
          channel: '*',
          series: [],
          properties: {
            ...EMPTY_MARKS,
            type: {
              value: MarkType.Text,
              automatic: false,
            },
            text: {
              values: [
                {
                  id: shortId(),
                  formula: `DATETRUNC('${timeGrain}', [${timestampAttr.name}])`,
                  variable: {
                    type: VariableType.DISCRETE,
                  },
                },
                {
                  id: shortId(),
                  formula: `[${metricAttr.name}]`,
                  variable: {
                    type: VariableType.DISCRETE,
                  },
                },
                ...dimAttrs.map((attr) => ({
                  id: shortId(),
                  formula: `[${attr.name}]`,
                  variable: {
                    type: VariableType.DISCRETE,
                  },
                })),
              ],
            },
          },
        },
      };
    }
    case 'line':
    case 'bar': {
      const seriesField: EncField & { id: string } = {
        id: shortId(),
        formula: `[${metricAttr.name}]`,
        variable: {
          type: VariableType.CONTINUOUS,
        },
      };

      return <Encoding>{
        ...EMPTY_ENCODING,
        x: [
          {
            id: shortId(),
            formula: `DATETRUNC('${timeGrain}', [${timestampAttr.name}])`,
            variable: {
              type: VariableType.CONTINUOUS,
            },
          },
        ],
        y: [seriesField],
        marks: {
          channel: 'y',
          properties: EMPTY_MARKS,
          series: [
            {
              field: seriesField.id,
              properties: {
                ...EMPTY_MARKS,
                type: {
                  value: vizType === 'line' ? MarkType.Line : MarkType.Bar,
                  automatic: false,
                },
              },
            },
          ],
        },
      };
    }
  }
}

export function isRequiredField(metric: MetricDomain.Metric | undefined, field: CompiledFormula): boolean | undefined {
  const attr = metric?.attrs.find((attr) => attr.name === field.attrName);
  // there's a race condition here where the field is not contained in the metric
  if (attr) {
    return attr.type !== MetricDomain.AttrType.Dimension;
  }
}
